import React, { useState } from "react";

import Box from "components/common/Box";
import Image from "components/common/Image";
import Modal from "components/common/Modal";
import IconButton from "components/common/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { marketPulseTheme as theme } from "./marketPulseTheme";

const MarketPulseImageViewer = ({
  images = [],
  activeIndex = 0,
  onActiveIndexChange = () => {},
  title = "",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!images.length) {
    return null;
  }

  const activeImage = images[activeIndex];
  const hasMultipleImages = images.length > 1;
  const hasPreviousImage = activeIndex > 0;
  const hasNextImage = activeIndex < images.length - 1;

  const handleImageChange = (direction) => {
    if (direction === "next" && activeIndex < images.length - 1) {
      onActiveIndexChange(activeIndex + 1);
    }
    if (direction === "prev" && activeIndex > 0) {
      onActiveIndexChange(activeIndex - 1);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: { xs: "100%", sm: theme.chartWidth },
          height: { xs: 220, sm: theme.chartHeight },
          borderRadius: theme.chartRadius,
          overflow: "hidden",
          flexShrink: 0,
          cursor: "pointer",
        }}
        onClick={() => setIsModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsModalOpen(true);
          }
        }}
      >
        {hasMultipleImages ? (
          <>
            {hasPreviousImage ? (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleImageChange("prev");
                }}
                sx={{
                  position: "absolute",
                  left: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(18, 42, 99, 0.55)",
                  color: theme.textPrimary,
                  width: 26,
                  height: 26,
                  "&:hover": { backgroundColor: "rgba(18, 42, 99, 0.75)" },
                }}
                aria-label="Previous image"
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
            {hasNextImage ? (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleImageChange("next");
                }}
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(18, 42, 99, 0.55)",
                  color: theme.textPrimary,
                  width: 26,
                  height: 26,
                  "&:hover": { backgroundColor: "rgba(18, 42, 99, 0.75)" },
                }}
                aria-label="Next image"
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
          </>
        ) : null}

        <Image
          src={activeImage.url}
          alt={title || "Market chart"}
          objectFit="cover"
          height="100%"
          rootStyle={{ width: "100%", height: "100%" }}
          imgStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} title={title || "Chart"} size="lg">
        <Box sx={{ position: "relative" }}>
          {hasMultipleImages ? (
            <>
              {hasPreviousImage ? (
                <IconButton
                  onClick={() => handleImageChange("prev")}
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: "rgba(18, 42, 99, 0.65)",
                    color: theme.textPrimary,
                  }}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon />
                </IconButton>
              ) : null}
              {hasNextImage ? (
                <IconButton
                  onClick={() => handleImageChange("next")}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: "rgba(18, 42, 99, 0.65)",
                    color: theme.textPrimary,
                  }}
                  aria-label="Next image"
                >
                  <ChevronRightIcon />
                </IconButton>
              ) : null}
            </>
          ) : null}
          <Image
            src={activeImage.url}
            alt={title || "Market chart"}
            objectFit="contain"
            height="auto"
            rootStyle={{ maxHeight: "70vh", width: "100%", px: hasMultipleImages ? 5 : 0 }}
            imgStyle={{ maxHeight: "65vh", width: "100%" }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default MarketPulseImageViewer;
